import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
  DialogTrigger, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DollarSign, Plus, RefreshCw, Paperclip, Send, XCircle, Eye,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_VARIANT = {
  DRAFT: 'secondary',
  SUBMITTED: 'default',
  APPROVED: 'default',
  PAID: 'success',
  REJECTED: 'destructive',
  CANCELLED: 'secondary',
};

const today = () => new Date().toISOString().slice(0, 10);

export default function MyExpensesPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ category_code: '', amount: '', expense_date: today(), description: '', notes: '' });
  const [receiptFile, setReceiptFile] = useState(null);

  const [viewOpen, setViewOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [receiptBlobUrl, setReceiptBlobUrl] = useState(null);
  const [receiptLoading, setReceiptLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [expRes, catRes] = await Promise.all([
        api.get('/expenses/my'),
        api.get('/expenses/categories'),
      ]);
      setItems(expRes.data.data || []);
      setCategories(catRes.data.data || []);
    } catch (e) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onVis = () => { if (!document.hidden) load(); };
    window.addEventListener('focus', load);
    document.addEventListener('visibilitychange', onVis);
    return () => { window.removeEventListener('focus', load); document.removeEventListener('visibilitychange', onVis); };
  }, [load]);

  useEffect(() => () => { if (receiptBlobUrl) URL.revokeObjectURL(receiptBlobUrl); }, [receiptBlobUrl]);

  const resetForm = () => { setForm({ category_code: '', amount: '', expense_date: today(), description: '', notes: '' }); setReceiptFile(null); };

  const submit = async () => {
    if (!form.category_code) return toast.error('Choose a category');
    if (!form.amount || parseFloat(form.amount) <= 0) return toast.error('Enter a positive amount');
    if (!form.expense_date) return toast.error('Choose the expense date');
    if (!form.description?.trim()) return toast.error('Describe the expense');
    setCreating(true);
    try {
      const createRes = await api.post('/expenses', {
        category_code: form.category_code,
        amount: parseFloat(form.amount),
        expense_date: form.expense_date,
        description: form.description.trim(),
        notes: form.notes?.trim() || undefined,
      });
      const created = createRes.data.data;
      if (receiptFile) {
        const fd = new FormData();
        fd.append('file', receiptFile);
        try {
          await api.post(`/expenses/${created.id}/receipt`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (e) {
          toast.error(e.response?.data?.error?.message || 'Receipt upload failed — the draft was saved but no receipt is attached.');
        }
        try {
          await api.post(`/expenses/${created.id}/submit`);
          toast.success('Expense submitted for approval');
        } catch (e) {
          toast.warning('Draft saved (with receipt) — submit failed: ' + (e.response?.data?.error?.message || 'unknown'));
        }
      } else {
        toast.success('Draft saved. Attach a receipt to submit it.');
      }
      setDialogOpen(false);
      resetForm();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.error?.message || e.response?.data?.detail || 'Failed to create expense');
    } finally { setCreating(false); }
  };

  const cancelExpense = async (id) => {
    try { await api.post(`/expenses/${id}/cancel`); toast.success('Expense cancelled'); await load(); }
    catch (e) { toast.error(e.response?.data?.error?.message || 'Cancel failed'); }
  };

  const openDetail = async (e) => {
    setSelected(e);
    setViewOpen(true);
    if (receiptBlobUrl) { URL.revokeObjectURL(receiptBlobUrl); setReceiptBlobUrl(null); }
    if (e.has_receipt) {
      setReceiptLoading(true);
      try {
        const res = await api.get(`/expenses/${e.id}/receipt`, { responseType: 'blob' });
        setReceiptBlobUrl(URL.createObjectURL(res.data));
      } catch (_) { /* silent */ }
      finally { setReceiptLoading(false); }
    }
  };

  const attachAndSubmit = async (row, file) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/expenses/${row.id}/receipt`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await api.post(`/expenses/${row.id}/submit`);
      toast.success('Receipt attached and expense submitted');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.error?.message || 'Attach/submit failed');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-expenses-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">My Expenses</h1>
          <p className="text-muted-foreground mt-1">Submit reimbursement claims for out-of-pocket costs</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { setRefreshing(true); load(); }} disabled={refreshing} data-testid="my-expenses-refresh">
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />Refresh
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="my-expenses-new"><Plus className="h-4 w-4 mr-2" />New Expense</Button>
            </DialogTrigger>
            <DialogContent data-testid="my-expenses-dialog">
              <DialogHeader>
                <DialogTitle>New Expense Claim</DialogTitle>
                <DialogDescription>Attach a receipt to submit. Without a receipt the claim is saved as a draft.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Category</Label>
                  <Select value={form.category_code} onValueChange={(v) => setForm({ ...form, category_code: v })}>
                    <SelectTrigger data-testid="my-expenses-category"><SelectValue placeholder="Choose a category" /></SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Amount (AUD)</Label>
                    <Input type="number" min="0" step="0.01" inputMode="decimal" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="my-expenses-amount" />
                  </div>
                  <div>
                    <Label>Date</Label>
                    <Input type="date" max={today()} value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} data-testid="my-expenses-date" />
                  </div>
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Where and why was this expense incurred?" data-testid="my-expenses-desc" />
                </div>
                <div>
                  <Label>Receipt</Label>
                  <Input type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,application/pdf" onChange={(e) => setReceiptFile(e.target.files?.[0] || null)} data-testid="my-expenses-receipt" />
                  <p className="text-xs text-muted-foreground mt-1">Attach a photo or PDF. Max 10 MB. Attaching a receipt submits the claim.</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button onClick={submit} disabled={creating} data-testid="my-expenses-submit">
                  <Send className="h-4 w-4 mr-2" />{creating ? 'Saving…' : (receiptFile ? 'Submit for approval' : 'Save draft')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <Card><CardContent className="py-16 text-center text-muted-foreground">Loading…</CardContent></Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <DollarSign className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">You have no expense claims yet.</p>
            <p className="text-xs text-muted-foreground mt-1">Tap &quot;New Expense&quot; to submit one.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((e) => {
            const canCancel = e.status === 'DRAFT' || e.status === 'SUBMITTED';
            const isDraft = e.status === 'DRAFT';
            return (
              <Card key={e.id} data-testid={`my-expenses-row-${e.id}`}>
                <CardHeader className="pb-2 flex flex-row items-start justify-between space-y-0">
                  <div>
                    <CardTitle className="text-base">{categories.find((c) => c.code === e.category_code)?.name || e.category_code} · {e.currency} {e.amount?.toFixed?.(2) ?? e.amount}</CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">{e.expense_date} · {e.description}</p>
                  </div>
                  <Badge variant={STATUS_VARIANT[e.status] || 'default'} data-testid={`my-expenses-status-${e.id}`}>{e.status}</Badge>
                </CardHeader>
                <CardContent>
                  {e.review_notes && (
                    <p className="text-sm mb-2"><span className="text-muted-foreground">Reviewer note: </span>{e.review_notes}</p>
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => openDetail(e)} data-testid={`my-expenses-view-${e.id}`}>
                      <Eye className="h-4 w-4 mr-1.5" />View
                    </Button>
                    {isDraft && !e.has_receipt && (
                      <label className="inline-flex">
                        <input type="file" hidden accept="image/*,application/pdf" onChange={(ev) => ev.target.files?.[0] && attachAndSubmit(e, ev.target.files[0])} data-testid={`my-expenses-attach-${e.id}`} />
                        <span className="inline-flex items-center px-3 py-1.5 border rounded-md text-sm hover:bg-muted cursor-pointer">
                          <Paperclip className="h-4 w-4 mr-1.5" />Attach & submit
                        </span>
                      </label>
                    )}
                    {isDraft && e.has_receipt && (
                      <Button size="sm" variant="secondary" onClick={async () => { try { await api.post(`/expenses/${e.id}/submit`); toast.success('Submitted'); await load(); } catch (err) { toast.error(err.response?.data?.error?.message || 'Submit failed'); } }} data-testid={`my-expenses-send-${e.id}`}>
                        <Send className="h-4 w-4 mr-1.5" />Submit for approval
                      </Button>
                    )}
                    {canCancel && (
                      <Button size="sm" variant="outline" onClick={() => cancelExpense(e.id)} data-testid={`my-expenses-cancel-${e.id}`}>
                        <XCircle className="h-4 w-4 mr-1.5" />Cancel
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent data-testid="my-expenses-detail-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Expense Detail</DialogTitle>
            <DialogDescription>Full details and receipt preview</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-2 text-sm">
              <div><span className="text-muted-foreground">Category: </span>{categories.find((c) => c.code === selected.category_code)?.name || selected.category_code}</div>
              <div><span className="text-muted-foreground">Amount: </span>{selected.currency} {selected.amount?.toFixed?.(2) ?? selected.amount}</div>
              <div><span className="text-muted-foreground">Date: </span>{selected.expense_date}</div>
              <div><span className="text-muted-foreground">Status: </span><Badge variant={STATUS_VARIANT[selected.status]}>{selected.status}</Badge></div>
              <div><span className="text-muted-foreground">Description: </span>{selected.description}</div>
              {selected.notes && <div><span className="text-muted-foreground">Notes: </span>{selected.notes}</div>}
              {selected.review_notes && <div><span className="text-muted-foreground">Reviewer note: </span>{selected.review_notes}</div>}
              {selected.has_receipt ? (
                <div className="pt-2">
                  <p className="text-muted-foreground mb-1">Receipt:</p>
                  {receiptLoading ? <p className="text-xs">Loading…</p>
                    : receiptBlobUrl ? (
                      selected.receipt_content_type?.startsWith('image/')
                        ? <img src={receiptBlobUrl} alt="receipt" className="rounded border max-h-96 object-contain" />
                        : <a href={receiptBlobUrl} target="_blank" rel="noreferrer" className="text-primary underline">Open receipt</a>
                    ) : <p className="text-xs text-destructive">Failed to load receipt</p>}
                </div>
              ) : <p className="text-xs text-muted-foreground pt-2">No receipt attached.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
